import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { getClients, getAllTerminals, getMyTerminals, getTerminalSummaries } from '../services/api';
import { FaServer, FaUsers, FaCheckCircle, FaTimesCircle, FaExclamationTriangle } from 'react-icons/fa';
import { getTerminalName } from '../utils/terminalNames';
import {
  getProfile, calcHumRisk, calcCondRisk, calcTempRisk,
  humStatus, condStatus, tempStatus,
} from '../utils/agronomyProfiles';

const StatCard = ({ icon: Icon, label, value, color, bgColor }) => (
  <div className="bg-white rounded-xl border border-gray-200 p-5 shadow-sm hover:shadow-md transition-shadow">
    <div className="flex items-center justify-between">
      <div>
        <p className="text-sm text-gray-500 font-medium">{label}</p>
        <p className="text-2xl font-bold text-gray-900 mt-1">{value}</p>
      </div>
      <div className={`w-12 h-12 rounded-lg ${bgColor} flex items-center justify-center`}>
        <Icon className={`w-5 h-5 ${color}`} />
      </div>
    </div>
  </div>
);



const riskVisual = (score) => {
  if (score >= 75) return { label: 'Critico', pill: 'bg-red-100 text-red-700 border-red-200' };
  if (score >= 40) return { label: 'Alerta', pill: 'bg-amber-100 text-amber-700 border-amber-200' };
  return { label: 'OK', pill: 'bg-emerald-100 text-emerald-700 border-emerald-200' };
};

const riskVisualFromStatus = (label, statusText, riskScore) => {
  const base = riskVisual(riskScore);
  if (label !== 'Riego' || typeof statusText !== 'string') return base;

  const text = statusText.toLowerCase();
  if (text.includes('estrés hídrico severo') || text.includes('suelo saturado')) {
    return { label: 'Critico', pill: 'bg-red-100 text-red-700 border-red-200' };
  }
  if (text.includes('baja humedad') || text.includes('exceso de humedad')) {
    return { label: 'Alerta', pill: 'bg-amber-100 text-amber-700 border-amber-200' };
  }
  if (text.includes('humedad óptima')) {
    return { label: 'OK', pill: 'bg-emerald-100 text-emerald-700 border-emerald-200' };
  }

  return base;
};

const normalizeConductivity = (value) => {
  const n = Number(value);
  if (!Number.isFinite(n)) return NaN;
  return n > 10 ? n / 100 : n;
};

const GaugeMeter = ({ label, value, min, max, unit, statusText, riskScore, reverseScale = false }) => {
  const safeValue = Number.isFinite(value) ? value : min;
  const bounded = Math.min(max, Math.max(min, safeValue));
  const rawPercent = ((bounded - min) / (max - min)) * 100;
  const percent = reverseScale ? 100 - rawPercent : rawPercent;
  const angle = -90 + (percent * 180) / 100;
  const visual = riskVisualFromStatus(label, statusText, riskScore);
  const gradientId = `gauge-${label.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`;

  return (
    <div className="bg-white rounded-xl border border-gray-200 p-3 shadow-sm">
      <div className="flex items-center justify-between mb-2">
        <p className="text-xs font-semibold text-gray-600 uppercase tracking-wider">{label}</p>
        <span className={`text-[10px] px-2 py-0.5 rounded-full border font-semibold ${visual.pill}`}>{visual.label}</span>
      </div>

      <div className="relative h-32 w-full">
        <svg viewBox="0 0 220 130" className="w-full h-full">
          <defs>
            <linearGradient id={gradientId} x1="0%" y1="0%" x2="100%" y2="0%">
              <stop offset="0%" stopColor="#22c55e" />
              <stop offset="55%" stopColor="#facc15" />
              <stop offset="80%" stopColor="#f97316" />
              <stop offset="100%" stopColor="#ef4444" />
            </linearGradient>
          </defs>
          <path d="M 20 110 A 90 90 0 0 1 200 110" fill="none" stroke="#e5e7eb" strokeWidth="16" strokeLinecap="round" />
          <path d="M 20 110 A 90 90 0 0 1 200 110" fill="none" stroke={`url(#${gradientId})`} strokeWidth="14" strokeLinecap="round" />
          <line x1="110" y1="110" x2="110" y2="34" stroke="#111827" strokeWidth="3" strokeLinecap="round" transform={`rotate(${angle}, 110, 110)`} />
          <circle cx="110" cy="110" r="6" fill="#111827" />
          <text x="110" y="82" textAnchor="middle" className="fill-gray-900" fontSize="21" fontWeight="700">{bounded.toFixed(1)}{unit}</text>
          <text x="110" y="99" textAnchor="middle" className="fill-gray-500" fontSize="11">riesgo {Math.round(riskScore)}%</text>
        </svg>
      </div>

      <div className="flex items-center justify-between text-[11px] text-gray-400 mt-1">
        <span>{reverseScale ? max : min}{unit}</span>
        <span>{reverseScale ? min : max}{unit}</span>
      </div>
      <p className="text-[11px] text-gray-600 mt-2 min-h-8">{statusText}</p>
    </div>
  );
};

const DashboardPage = ({ token, user }) => {
  const [clients, setClients] = useState([]);
  const [terminals, setTerminals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [summaries, setSummaries] = useState([]);
  const navigate = useNavigate();
  const isAdmin = user && user.role === 1;

  useEffect(() => {
    const loadData = async () => {
      try {
        if (isAdmin) {
          const [cRes, tRes] = await Promise.all([getClients(token), getAllTerminals(token)]);
          if (cRes.cod === 200) setClients(cRes.data);
          if (tRes.cod === 200) setTerminals(tRes.data);
        } else {
          const [tRes, sRes] = await Promise.all([getMyTerminals(token), getTerminalSummaries(token, 7)]);
          if (tRes.cod === 200) setTerminals(tRes.data);
          if (sRes.cod === 200) setSummaries(sRes.data || []);
        }
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, [token, isAdmin]);

  const activeTerminals = terminals.filter(t => t.estado).length;
  const inactiveTerminals = terminals.filter(t => !t.estado).length;
  const unassignedTerminals = terminals.filter(t => !t.id_cliente).length;
  const activeClients = clients.filter(c => c.estado === 'activo').length;

  const terminalMap = {};
  terminals.forEach(t => { terminalMap[t.guid] = t; });

  const buildAgronomy = (s, profile) => {
    const condValues = [
      normalizeConductivity(s.conductivity_soil_30cm),
      normalizeConductivity(s.conductivity_soil_60cm),
    ].filter(Number.isFinite);
    const condAvg = condValues.length ? condValues.reduce((acc, v) => acc + v, 0) / condValues.length : NaN;
    const humAvg = (Number(s.humidity_soil_30cm || 0) + Number(s.humidity_soil_60cm || 0)) / 2;
    const tempAvg = (Number(s.temperature_soil_30cm || 0) + Number(s.temperature_soil_60cm || 0)) / 2;

    const fertRisk = calcCondRisk(condAvg, profile);
    const waterRisk = calcHumRisk(humAvg, profile);
    const tempRisk = calcTempRisk(tempAvg, profile);
    const fert = condStatus(condAvg, profile);
    const water = humStatus(humAvg, profile);
    const temp = tempStatus(tempAvg, profile);

    return { condAvg, humAvg, tempAvg, fert, water, temp, fertRisk, waterRisk, tempRisk };
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-600" />
      </div>
    );
  }

  return (
    <div>
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Dashboard</h1>
        <p className="text-sm text-gray-500 mt-1">
          {isAdmin ? 'Resumen general del sistema' : 'Bienvenido al panel de control'}
        </p>
      </div>

      {isAdmin ? (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
            <StatCard icon={FaServer} label="Terminales Activas" value={activeTerminals} color="text-emerald-600" bgColor="bg-emerald-50" />
            <StatCard icon={FaTimesCircle} label="Terminales Inactivas" value={inactiveTerminals} color="text-red-600" bgColor="bg-red-50" />
            <StatCard icon={FaUsers} label="Clientes Activos" value={activeClients} color="text-blue-600" bgColor="bg-blue-50" />
            <StatCard icon={FaExclamationTriangle} label="Sin Asignar" value={unassignedTerminals} color="text-amber-600" bgColor="bg-amber-50" />
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="bg-white rounded-xl border border-gray-200 p-5 shadow-sm">
              <h3 className="text-sm font-semibold text-gray-800 uppercase tracking-wider mb-4">Últimos Clientes</h3>
              <div className="space-y-3">
                {clients.slice(0, 5).map(c => (
                  <div key={c.id} className="flex items-center justify-between py-2 border-b border-gray-100 last:border-0">
                    <div>
                      <p className="text-sm font-medium text-gray-800">{c.nombre}</p>
                      <p className="text-xs text-gray-400">{c.rut}</p>
                    </div>
                    <span className={`px-2 py-0.5 rounded-full text-[10px] font-medium ${
                      c.estado === 'activo' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                    }`}>
                      {c.estado}
                    </span>
                  </div>
                ))}
                {clients.length === 0 && <p className="text-sm text-gray-400 text-center py-4">No hay clientes registrados</p>}
              </div>
            </div>

            <div className="bg-white rounded-xl border border-gray-200 p-5 shadow-sm">
              <h3 className="text-sm font-semibold text-gray-800 uppercase tracking-wider mb-4">Últimas Terminales</h3>
              <div className="space-y-3">
                {terminals.slice(0, 5).map(t => (
                  <div key={t.guid} className="flex items-center justify-between py-2 border-b border-gray-100 last:border-0">
                    <div>
                      <p className="text-sm font-mono font-medium text-gray-800">{t.guid}</p>
                      <p className="text-xs text-gray-400">{t.ubicacion || 'Sin ubicación'}</p>
                    </div>
                    <div className="flex items-center gap-2">
                      {t.cliente_nombre && <span className="text-xs text-gray-500">{t.cliente_nombre}</span>}
                      {t.estado ? (
                        <FaCheckCircle className="w-4 h-4 text-emerald-500" />
                      ) : (
                        <FaTimesCircle className="w-4 h-4 text-red-500" />
                      )}
                    </div>
                  </div>
                ))}
                {terminals.length === 0 && <p className="text-sm text-gray-400 text-center py-4">No hay terminales registradas</p>}
              </div>
            </div>
          </div>
        </>
      ) : (
        <div className="space-y-6">
          <div className="bg-white rounded-xl border border-gray-200 p-5 shadow-sm">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 mb-4">
              <h3 className="text-sm font-semibold text-gray-800 uppercase tracking-wider">Panel Agronómico Visual</h3>
              <div className="flex gap-2">
                <button onClick={() => navigate('/terminales')} className="px-3 py-1.5 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors text-xs font-semibold">
                  Mis Terminales
                </button>
                <button onClick={() => navigate('/graficos')} className="px-3 py-1.5 bg-white text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors text-xs font-semibold">
                  Ver Gráficos
                </button>
              </div>
            </div>
            <div className="space-y-4">
              {summaries.map((s) => {
                const term = terminalMap[s.guid] || {};
                const p = getProfile(term.tipo_cultivo, term.tipo_suelo);
                const a = buildAgronomy(s, p);
                return (
                  <div key={s.guid} className="border border-gray-100 rounded-xl p-4 bg-gradient-to-br from-white via-emerald-50/30 to-white">
                    <div className="mb-3 flex items-center justify-between">
                      <div>
                        <p className="font-semibold text-gray-900">{getTerminalName(s)}</p>
                        <p className="text-[11px] text-gray-500 font-mono">GUID: {s.guid}</p>
                      </div>
                      {term.tipo_cultivo || term.tipo_suelo ? (
                        <span className="text-[10px] text-gray-400 bg-gray-50 border border-gray-200 rounded px-2 py-0.5">{term.tipo_cultivo || '—'} / {term.tipo_suelo ? ({ FrancoArenoso: 'F. Arenoso', Franco: 'Franco', Arcilloso: 'Arcilloso' }[term.tipo_suelo] || term.tipo_suelo) : '—'}</span>
                      ) : null}
                    </div>
                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-3 mb-3">
                      <GaugeMeter label="Riego" value={a.humAvg} min={0} max={100} unit="%" statusText={a.water} riskScore={a.waterRisk} reverseScale />
                      <GaugeMeter label="Fertilización" value={a.condAvg} min={0} max={3.5} unit="" statusText={a.fert} riskScore={a.fertRisk} />
                      <GaugeMeter label="Temperatura suelo" value={a.tempAvg} min={0} max={40} unit="°C" statusText={a.temp} riskScore={a.tempRisk} />
                    </div>
                  </div>
                );
              })}
              {summaries.length === 0 && <p className="text-sm text-gray-400 text-center py-4">Sin datos de lecturas aún</p>}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DashboardPage;
